import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  Injector,
  NgZone,
  OnDestroy,
  OnInit,
  signal,
  ViewChild
} from '@angular/core';
import {RouterLink} from '@angular/router';
import {BaseComponent} from '../../base.component';
import {AccExpansionPanelComponent} from '../acc-expansion-panel/acc-expansion-panel.component';
import {HOME_SCHEMA} from '../../../common/schema';
import {IconName, LucideIconComponent} from '../../../util/component/lucide-icon/lucide-icon.component';
import {PriceCardComponent} from './price-card/price-card.component';
import {BenefitCalcComponent} from './benefit-calc/benefit-calc.component';

interface Step {
  icon: IconName;
  title: string;
  body: string;
  time: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    RouterLink,
    AccExpansionPanelComponent,
    LucideIconComponent,
    PriceCardComponent,
    BenefitCalcComponent
  ],
  // <plaque-3d> و <vault-shield-3d> عناصر سفارشی‌اند که فقط در مرورگر تعریف می‌شوند
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent extends BaseComponent implements OnInit, OnDestroy {

  /** همان مقصد دکمه «ورود / ثبت نام» در هدر */
  readonly registerHref = 'https://web.baazar.ir/';

  readonly trustItems: { icon: IconName; label: string }[] = [
    {icon: 'scale', label: 'نظارت وزارت صمت'},
    {icon: 'landmark', label: 'کارگشایی بانک ملی'},
    {icon: 'badge-check', label: 'نماد اعتماد الکترونیکی'},
    {icon: 'credit-card', label: 'درگاه پرداخت بانکی'},
    {icon: 'shield-check', label: 'مجوز اتحادیه طلا و جواهر'}
  ];

  readonly features: { icon: IconName; title: string; body: string }[] = [
    {
      icon: 'trending-up',
      title: 'خرید و فروش لحظه‌ای',
      body: 'قیمت‌ها لحظه‌ای با بازار طلای تهران به‌روز می‌شوند. در کسری از ثانیه معامله کنید.'
    },
    {
      icon: 'file-text',
      title: 'گزارش مالی شفاف',
      body: 'هر تراکنش پولی و طلایی شما در اپلیکیشن ثبت و قابل پیگیری است.'
    },
    {
      icon: 'shield-check',
      title: 'صندوق سرمایه‌گذاری امن',
      body: 'دارایی طلای شما تحت نظارت و در کارگشایی بانک ملی نگهداری می‌شود.'
    },
    {
      icon: 'banknote',
      title: 'تسویهٔ ریالی سریع',
      body: 'هر زمان خواستید طلا را بفروشید و وجه را مستقیم به حساب بانکی خود دریافت کنید.'
    }
  ];

  readonly deliveryTicks = [
    'پلاک ۲٫۵، ۵ و ۱۰ گرمی یا طلای آب‌شده از ۳۰ گرم',
    'بسته‌بندی پلمب‌شده با هولوگرام ضدجعل',
    'تحویل حضوری یا نیابتی',
    'کد حک‌شده‌ی قابل استعلام روی هر پلاک'
  ];

  readonly steps: Step[] = [
    {
      icon: 'smartphone',
      title: 'ثبت‌نام با شمارهٔ موبایل',
      body: 'شمارهٔ خود را وارد کنید و با رمز یک‌بارمصرف وارد شوید. بدون فرم طولانی.',
      time: 'کمتر از ۱ دقیقه'
    },
    {
      icon: 'user-check',
      title: 'احراز هویت',
      body: 'مدارک هویتی را بارگذاری کنید؛ حساب بلافاصله پس از استعلام تأیید می‌شود.',
      time: '۲ دقیقه'
    },
    {
      icon: 'credit-card',
      title: 'اتصال حساب بانکی',
      body: 'شمارهٔ شبای به‌نام خودتان را ثبت کنید تا واریز و تسویهٔ ریالی فعال شود.',
      time: '۱ دقیقه'
    },
    {
      icon: 'coins',
      title: 'اولین خرید طلا',
      body: 'کیف پول را شارژ کنید و با هر مبلغی، حتی کسری از گرم، اولین طلایتان را بخرید.',
      time: 'لحظه‌ای'
    }
  ];

  readonly appTicks = [
    'ورود امن با اثر انگشت و رمز یک‌بارمصرف',
    'هشدار قیمت دلخواه شما',
    'کیف پول ریالی و طلایی یکپارچه'
  ];

  /** اسلایدهای ویدیو؛ فعلاً یکی است و فلش‌ها و نقطه‌ها فقط با بیش از یکی دیده می‌شوند */
  readonly videos = [
    {src: '/video/ronika.mp4', title: 'ویدیوی معرفی بازر', note: 'برای پخش کلیک کنید'}
  ];

  readonly videoIndex = signal(0);
  readonly playing = signal<number | null>(null);

  readonly activeStep = signal(0);
  private stepsLocked = false;
  private stepsTimer?: ReturnType<typeof setInterval>;

  /**
   * عددهای هیرو. مقدار اولیه عدد نهایی است تا خروجی سرور و حالت بدون
   * جاوااسکریپت درست باشد؛ در مرورگر از صفر شمرده می‌شوند (مثل صفحه‌ی درباره ما).
   */
  readonly activeUsers = signal(this.formatCount(150000));
  readonly tonsTraded = signal(this.formatCount(2));

  @ViewChild('stats') private statsRef?: ElementRef<HTMLElement>;

  private observers: IntersectionObserver[] = [];
  private countFrame = 0;

  constructor(injector: Injector, private zone: NgZone, private host: ElementRef<HTMLElement>) {
    super(injector);

    // ‏afterNextRender پس از hydration اجرا می‌شود؛ همه‌ی کارهای مرورگری این‌جاست
    afterNextRender(() => {
      this.startStepsCycle();
      this.setupCounters();
      this.setupScenes();
    }, {injector});
  }

  override ngOnInit() {
    super.ngOnInit();

    this.seoService.updateMetaData({
      title: 'بازر | خرید و فروش طلای آب شده بدون اجرت و پلاک طلا',
      description: 'سرمایه گذاری، خرید و فروش آنلاین طلا بصورت آب شده و پلاک گرمی به مقدار دلخواه (حتی یک صدم گرم) هفت روز هفته از بازر اولین اپلیکیشن خرید و فروش طلا به صورت آنلاین در ایران.',
      canonical: 'https://baazar.ir/',
      jsonLd: HOME_SCHEMA
    });
  }

  ngOnDestroy() {
    this.observers.forEach(o => o.disconnect());
    if (this.stepsTimer) clearInterval(this.stepsTimer);
    if (this.countFrame) cancelAnimationFrame(this.countFrame);
  }

  // ── گام‌های عضویت: هر ۳٫۲ ثانیه یک قدم جلو می‌رود تا کاربر یکی را انتخاب کند ──

  pickStep(index: number) {
    this.stepsLocked = true;
    if (this.stepsTimer) clearInterval(this.stepsTimer);
    this.activeStep.set(index);
  }

  private startStepsCycle() {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    // بیرون از zone: تایمر همیشگی نباید جلوی پایدار شدن برنامه را بگیرد
    this.zone.runOutsideAngular(() => {
      this.stepsTimer = setInterval(() => {
        if (this.stepsLocked) return;
        this.activeStep.update(i => (i + 1) % this.steps.length);
      }, 3200);
    });
  }

  // ── ویدیو ──

  playVideo(index: number, video: HTMLVideoElement) {
    this.playing.set(index);
    video.play().catch(() => {
      // اگر مرورگر پخش را رد کرد، کنترل‌های خود ویدیو در دسترس است
    });
  }

  goVideo(index: number) {
    const n = this.videos.length;
    this.videoIndex.set((index + n) % n);
  }

  // ── شمارش عددهای هیرو ──

  private setupCounters() {
    const target = this.statsRef?.nativeElement;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!target || reduce || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      observer.disconnect();
      this.countUp();
    }, {threshold: 0.35});
    observer.observe(target);
    this.observers.push(observer);
  }

  private countUp(duration = 1400) {
    const start = performance.now();
    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
    this.zone.runOutsideAngular(() => {
      const step = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        const k = p >= 1 ? 1 : easeOut(p);
        this.activeUsers.set(this.formatCount(150000 * k));
        this.tonsTraded.set(this.formatCount(2 * k));
        if (p < 1) this.countFrame = requestAnimationFrame(step);
      };
      this.countFrame = requestAnimationFrame(step);
    });
  }

  private formatCount(value: number) {
    return Math.round(value).toLocaleString('fa-IR') + '+';
  }

  // ── صحنه‌های سه‌بعدی: three.js فقط وقتی این بخش‌ها نزدیک دید برسند گرفته می‌شود ──

  private setupScenes() {
    const targets = Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>('plaque-3d, vault-shield-3d'));
    if (!targets.length) return;

    const load = () => {
      this.zone.runOutsideAngular(() => {
        import('./scenes/home-scenes').then(m => m.defineHomeScenes());
      });
    };

    if (typeof IntersectionObserver === 'undefined') {
      load();
      return;
    }

    const observer = new IntersectionObserver(entries => {
      if (!entries.some(e => e.isIntersecting)) return;
      observer.disconnect();
      load();
    }, {rootMargin: '600px 0px'});
    targets.forEach(t => observer.observe(t));
    this.observers.push(observer);
  }
}
